import React, { useState, useEffect, useRef } from 'react';
import { Check, ShoppingCart, AlertCircle, X, CreditCard, ExternalLink, Star, Upload, Clock, KeyRound, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AqmOptiToolPage = () => {
  const { isAuthenticated, loginWithDiscord, token } = useAuth();
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(null); // { type: 'ticket'|'order', message, discord_url }
  const [showPayModal, setShowPayModal] = useState(false);
  const [loadingMethod, setLoadingMethod] = useState(null); // 'stripe' | 'paypal' | 'paysafecard'
  const [altSuccess, setAltSuccess] = useState(null); // { method, discord_url }

  // Pricing + review discount (fetched from backend)
  const [config, setConfig] = useState({ price: 3, discount_percent: 20, discounted_price: 2.4 });
  const [reviewStatus, setReviewStatus] = useState(null); // { review: {...} | null, has_approved: bool }
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewPlatform, setReviewPlatform] = useState('discord');
  const [reviewFile, setReviewFile] = useState(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const reviewFileInputRef = useRef(null);

  // License owned by the connected Discord user (auto-detected)
  const [license, setLicense] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch pricing config
  useEffect(() => {
    fetch(`${API_URL}/api/aqmoptitool/config`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setConfig(d))
      .catch(() => {});
  }, []);

  // Detect if the connected Discord user already owns a license
  useEffect(() => {
    if (!token) { setLicense(null); return; }
    fetch(`${API_URL}/api/aqmoptitool/my-license`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setLicense(d && d.has_license ? d : null))
      .catch(() => {});
  }, [token]);

  const copyLicense = () => {
    if (!license?.license_key) return;
    navigator.clipboard?.writeText(license.license_key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  // Fetch user's review status when authenticated
  const reloadReview = () => {
    if (!token) { setReviewStatus(null); return; }
    fetch(`${API_URL}/api/aqmoptitool/my-review`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setReviewStatus(d))
      .catch(() => {});
  };
  useEffect(() => { reloadReview(); /* eslint-disable-next-line */ }, [token]);

  const totalDiscount = reviewStatus?.total_discount ?? 0;
  const hasApprovedReview = totalDiscount > 0;
  const isPremium = (license?.plan || '').toLowerCase() === 'premium';
  const finalPrice = hasApprovedReview ? (reviewStatus?.discounted_price ?? config.price) : config.price;
  const formatPrice = (p) => Number.isInteger(p) ? `${p}€` : `${p.toFixed(2).replace('.', ',')}€`;
  // Sources with a configured % > 0 (from my-review if available, else public config)
  const discountSources = (reviewStatus?.sources || config.sources || []).filter(s => (s.percent || 0) > 0);
  const anySourceLeft = discountSources.some(s => s.status !== 'approved' && s.status !== 'pending');

  const features = [
    "Nettoyage Windows complet",
    "Appliquer des fix Windows rapidement",
    "Configuration Paramètre Fortnite",
    "Importer des profils OBS optimisés",
    "Choisir les applications au démarrage",
    "Gamemode intégré"
  ];

  const openPay = () => {
    if (!isAuthenticated) {
      loginWithDiscord();
      return;
    }
    setError(null);
    setAltSuccess(null);
    setShowPayModal(true);
  };

  const handleStripe = async () => {
    setLoadingMethod('stripe');
    setError(null);
    try {
      const affiliateCode = localStorage.getItem('affiliate_code');
      const res = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_type: 'aqmoptitool',
          affiliate_code: affiliateCode,
          promo_code: null
        })
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        if (res.status === 409) {
          setShowPayModal(false);
          setConflict({ type: 'order', message: e.detail || 'Tu as déjà une commande active.', discord_url: null });
          return;
        }
        throw new Error(e.detail || 'Erreur Stripe');
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erreur lors de la redirection Stripe');
    } finally {
      setLoadingMethod(null);
    }
  };

  const handleAlt = async (method) => {
    setLoadingMethod(method);
    setError(null);
    try {
      const affiliateCode = localStorage.getItem('affiliate_code');
      const res = await fetch(`${API_URL}/api/create-payment-ticket`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_method: method,
          service_type: 'aqmoptitool',
          service_name: 'AqmOptiTool',
          total_price: finalPrice,
          options: {},
          referral_code: null,
          affiliate_code: affiliateCode
        })
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        if (res.status === 409) {
          // Extract Discord URL from message if present
          const detail = e.detail || '';
          const urlMatch = detail.match(/https?:\/\/[^\s]+/);
          const discord_url = urlMatch ? urlMatch[0] : null;
          const isTicket = detail.toLowerCase().includes('ticket');
          setShowPayModal(false);
          setConflict({
            type: isTicket ? 'ticket' : 'order',
            method,
            message: detail,
            discord_url,
          });
          return;
        }
        throw new Error(e.detail || 'Erreur lors de la création du ticket');
      }
      const data = await res.json();
      setShowPayModal(false);
      setAltSuccess({ method, discord_url: data.discord_url, ticket_number: data.ticket_number });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoadingMethod(null);
    }
  };

  const submitReview = async () => {
    if (!reviewFile) { setReviewError('Ajoute un screenshot'); return; }
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      const fd = new FormData();
      fd.append('platform', reviewPlatform);
      fd.append('screenshot', reviewFile);
      const res = await fetch(`${API_URL}/api/aqmoptitool/review-request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Envoi impossible');
      reloadReview();
      setShowReviewModal(false);
      setReviewFile(null);
    } catch (e) {
      setReviewError(e.message);
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Method display config
  const methodConfig = {
    paypal: { label: 'PayPal', color: 'text-blue-400', accent: 'bg-blue-500/15', border: 'border-blue-500/40' },
    paysafecard: { label: 'Paysafecard', color: 'text-yellow-400', accent: 'bg-yellow-500/15', border: 'border-yellow-500/40' },
  };

  return (
    <div className="min-h-screen py-16 px-4" data-testid="aqmoptitool-page">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 text-xs font-medium text-cyan-400 bg-cyan-500/10 rounded-full border border-cyan-500/20 mb-4">
            Outil Premium
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Aqm<span className="text-cyan-400">OptiTool</span>
          </h1>
          <p className="text-gray-400 text-base max-w-lg mx-auto">
            L'outil tout-en-un pour optimiser ton PC Windows en quelques clics. Simple, rapide et efficace.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/40 rounded-lg text-orange-400 text-center text-sm font-medium flex items-center justify-center gap-2" data-testid="error-message">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Ta licence AqmOptiTool — détectée automatiquement via ton compte Discord */}
        {license && (
          <div className="mb-8 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.08] to-transparent p-6" data-testid="aqmopti-my-license">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                <KeyRound className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm md:text-base mb-1">
                  {isPremium ? 'Tu possèdes déjà AqmOptiTool ✅' : 'Ta licence AqmOptiTool'}
                </h3>
                <p className="text-gray-400 text-xs md:text-sm mb-3">
                  {isPremium
                    ? "Voici ta clé de licence liée à ton compte Discord. Garde-la précieusement."
                    : "Voici ta clé actuelle. Passe en Premium ci-dessous pour débloquer toutes les fonctionnalités."}
                </p>
                <div className="flex items-center gap-2 bg-black/40 border border-zinc-700 rounded-lg px-3 py-2.5">
                  <code className="flex-1 text-cyan-300 text-sm font-mono truncate" data-testid="aqmopti-license-key">{license.license_key}</code>
                  <button
                    onClick={copyLicense}
                    className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 text-xs font-medium transition-colors"
                    data-testid="aqmopti-license-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copied ? 'Copié !' : 'Copier'}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="aqmopti-license-meta">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                    isPremium
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      : 'bg-zinc-700/40 border-zinc-600 text-gray-300'
                  }`} data-testid="aqmopti-license-plan">
                    {isPremium ? '★ Premium' : `Plan ${license.plan || 'Standard'}`}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${
                    license.status === 'expired'
                      ? 'bg-red-500/10 border-red-500/30 text-red-300'
                      : 'bg-green-500/10 border-green-500/30 text-green-300'
                  }`}>
                    {license.status === 'expired' ? 'Expirée' : 'Active'}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-zinc-800 border border-zinc-700 text-gray-300">
                    Licence à vie
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* App Preview — 3D floating window */}
        <div className="mb-16 mt-2" style={{ perspective: '1600px' }}>
          <div className="relative group mx-auto max-w-3xl">
            {/* Glow behind the window */}
            <div className="absolute -inset-8 rounded-[2.5rem] bg-cyan-500/20 blur-[90px] opacity-70 pointer-events-none" aria-hidden="true" />
            <div className="absolute inset-x-12 -bottom-8 h-20 bg-cyan-400/25 blur-[55px] rounded-full pointer-events-none" aria-hidden="true" />

            {/* Tilted 3D window (straightens + lifts on hover) */}
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10 transition-transform duration-700 ease-out will-change-transform [transform:rotateX(13deg)_rotateY(-13deg)_scale(0.95)] group-hover:[transform:rotateX(0deg)_rotateY(0deg)_scale(1.02)]"
              style={{
                transformStyle: 'preserve-3d',
                boxShadow: '0 45px 95px -25px rgba(6,182,212,0.45), 0 25px 45px -18px rgba(0,0,0,0.65)',
              }}
            >
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_web-bot-nexus/artifacts/jlmdratz_image.png"
                alt="Interface AqmOptiTool — Dashboard"
                className="w-full h-auto block"
                loading="lazy"
              />
              {/* Subtle light sheen */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.03] to-white/[0.08] pointer-events-none" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Main Card - Prix + Features */}
        {!isPremium && (
        <div className="bg-zinc-900/60 rounded-2xl border border-zinc-800 p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            {/* Left - Features */}
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white mb-4">Ce qui est inclus</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Price & CTA */}
            <div className="lg:border-l lg:border-zinc-700 lg:pl-8 text-center lg:text-left">
              <div className="mb-4">
                {hasApprovedReview ? (
                  <div className="flex items-baseline gap-2 justify-center lg:justify-start">
                    <span className="text-lg font-medium text-gray-500 line-through">{formatPrice(config.price)}</span>
                    <span className="text-4xl font-black text-cyan-400" data-testid="aqmopti-final-price">{formatPrice(finalPrice)}</span>
                  </div>
                ) : (
                  <span className="text-4xl font-black text-white" data-testid="aqmopti-final-price">{formatPrice(finalPrice)}</span>
                )}
                <p className="text-gray-500 text-sm mt-1">Licence à vie</p>
                {hasApprovedReview && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-medium" data-testid="aqmopti-discount-badge">
                    <Star className="w-3 h-3 fill-current" /> −{totalDiscount}% réduction validée
                  </div>
                )}
              </div>
              <Button 
                onClick={openPay}
                className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 w-full lg:w-auto"
                data-testid="order-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {license ? 'Passer en Premium' : 'Obtenir'}
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Review/tag discount section — cumulative, per-source */}
        {isAuthenticated && !isPremium && discountSources.length > 0 && (
          <div className="mb-8 rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.06] to-transparent p-5 md:p-6" data-testid="aqmopti-review-section">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-11 h-11 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-sm md:text-base">
                  Cumule tes réductions {totalDiscount > 0 && <span className="text-cyan-400">· −{totalDiscount}% débloqués</span>}
                </h3>
                <p className="text-gray-400 text-xs md:text-sm">
                  Envoie une preuve (screenshot) pour chaque source. Chaque validation ajoute sa réduction.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {discountSources.map((s) => {
                const status = s.status; // approved | pending | rejected | null
                return (
                  <div
                    key={s.key}
                    className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3.5 py-3"
                    data-testid={`aqmopti-source-${s.key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">
                        {s.label} <span className="text-cyan-400 font-semibold">−{s.percent}%</span>
                      </p>
                      {status === 'rejected' && (
                        <p className="text-red-400 text-[11px] mt-0.5">Refusé — tu peux réessayer</p>
                      )}
                    </div>
                    {status === 'approved' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-500/15 border border-green-500/40 text-green-300 text-xs font-semibold whitespace-nowrap">
                        <Check className="w-3.5 h-3.5" /> Validé
                      </span>
                    ) : status === 'pending' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold whitespace-nowrap">
                        <Clock className="w-3.5 h-3.5" /> En attente
                      </span>
                    ) : (
                      <button
                        onClick={() => { setReviewPlatform(s.key); setReviewError(null); setReviewFile(null); setShowReviewModal(true); }}
                        className="whitespace-nowrap px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-semibold text-xs transition-colors"
                        data-testid={`aqmopti-source-submit-${s.key}`}
                      >
                        Envoyer preuve
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom info */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            Paiement sécurisé
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            Support Discord
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-500" />
            Mises à jour gratuites
          </span>
        </div>
      </div>

      {/* Payment modal */}
      {showPayModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => loadingMethod === null && setShowPayModal(false)}
          data-testid="aqmopti-pay-modal"
        >
          <div
            className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-cyan-400">Paiement</div>
                <div className="text-lg font-semibold text-white mt-0.5">AqmOptiTool — {formatPrice(finalPrice)}</div>
              </div>
              <button
                type="button"
                onClick={() => loadingMethod === null && setShowPayModal(false)}
                className="text-gray-400 hover:text-white"
                data-testid="aqmopti-pay-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <button
                type="button"
                onClick={handleStripe}
                disabled={loadingMethod !== null}
                className="w-full flex items-center justify-between p-4 rounded-xl border border-zinc-700 hover:border-cyan-500 bg-black/30 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
                data-testid="aqmopti-pay-stripe-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold text-sm">Carte bancaire (Stripe)</p>
                    <p className="text-gray-500 text-xs">Instantané, sécurisé</p>
                  </div>
                </div>
                {loadingMethod === 'stripe' ? (
                  <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span className="text-cyan-400 text-sm font-medium">→</span>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleAlt('paypal')}
                disabled={loadingMethod !== null}
                className="w-full flex items-center justify-between p-4 rounded-xl border border-zinc-700 hover:border-blue-500 bg-black/30 hover:bg-blue-500/10 transition-colors disabled:opacity-50"
                data-testid="aqmopti-pay-paypal-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-400 font-bold">
                    PP
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold text-sm">PayPal</p>
                    <p className="text-gray-500 text-xs">Ticket Discord, validation manuelle</p>
                  </div>
                </div>
                {loadingMethod === 'paypal' ? (
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span className="text-blue-400 text-sm font-medium">→</span>
                )}
              </button>
            </div>

            <div className="p-4 border-t border-zinc-800 text-[11px] text-gray-500 text-center">
              Paiement sécurisé • Licence à vie • Support Discord inclus
            </div>
          </div>
        </div>
      )}

      {/* Success popup — ticket Discord créé */}
      {altSuccess && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setAltSuccess(null)}
          data-testid="aqmopti-success-modal"
        >
          <div
            className="w-full max-w-md bg-zinc-900 border border-green-500/30 rounded-2xl shadow-[0_0_60px_-12px_rgba(34,197,94,0.4)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center border-b border-zinc-800">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center animate-pulse">
                <Check className="w-9 h-9 text-green-400" />
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-green-400 mb-1.5 font-semibold">Ticket créé</p>
              <h3 className="text-xl font-bold text-white">
                Ton salon Discord est ouvert !
              </h3>
              <p className="text-sm text-gray-400 mt-2 leading-relaxed">
                Un ticket {(methodConfig[altSuccess.method] || {}).label || altSuccess.method.toUpperCase()} a été créé sur le serveur Discord. Rejoins-le pour finaliser le paiement et recevoir AqmOptiTool.
              </p>
            </div>

            <div className="p-5 space-y-3">
              <a
                href={altSuccess.discord_url}
                target="_blank"
                rel="noreferrer"
                onClick={() => setAltSuccess(null)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold text-sm transition-colors"
                data-testid="aqmopti-success-discord-btn"
              >
                <ExternalLink className="w-4 h-4" />
                Rejoindre mon ticket Discord
              </a>
              <button
                type="button"
                onClick={() => setAltSuccess(null)}
                className="w-full px-4 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors"
                data-testid="aqmopti-success-close-btn"
              >
                Fermer
              </button>
            </div>

            <div className="px-5 pb-5 text-[11px] text-gray-500 text-center leading-relaxed">
              💡 Si tu ne vois pas le salon, vérifie d'avoir bien rejoint notre Discord.
              {altSuccess.ticket_number && (
                <> Ticket : <span className="font-mono text-gray-400">{altSuccess.ticket_number}</span></>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Conflict popup — already has ticket / order */}
      {conflict && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setConflict(null)}
          data-testid="aqmopti-conflict-modal"
        >
          <div
            className="w-full max-w-md bg-zinc-900 border border-orange-500/40 rounded-2xl shadow-[0_0_60px_-12px_rgba(249,115,22,0.4)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center border-b border-zinc-800">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
                <AlertCircle className="w-9 h-9 text-orange-400" />
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1.5 font-semibold">
                {conflict.type === 'ticket' ? 'Ticket déjà ouvert' : 'Commande en cours'}
              </p>
              <h3 className="text-xl font-bold text-white mb-2">
                {conflict.type === 'ticket'
                  ? 'Tu as déjà un ticket en cours'
                  : 'Une commande est déjà active'}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                {conflict.type === 'ticket' ? (
                  <>
                    Tu as déjà ouvert un ticket pour <span className="text-white font-semibold">AqmOptiTool</span>.
                    Pour éviter les doublons, finalise-le d'abord ou contacte le staff.
                  </>
                ) : (
                  <>
                    Tu as déjà une commande <span className="text-white font-semibold">AqmOptiTool</span> active.
                    Attends qu'elle soit terminée par le staff (commande <span className="font-mono text-cyan-400">*finish</span>) avant d'en lancer une nouvelle.
                  </>
                )}
              </p>
            </div>

            <div className="p-5 space-y-3">
              {conflict.discord_url && (
                <a
                  href={conflict.discord_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setConflict(null)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold text-sm transition-colors"
                  data-testid="aqmopti-conflict-discord-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Rejoindre mon ticket existant
                </a>
              )}
              <button
                type="button"
                onClick={() => setConflict(null)}
                className="w-full px-4 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors"
                data-testid="aqmopti-conflict-close-btn"
              >
                D'accord, j'ai compris
              </button>
            </div>

            <div className="px-5 pb-5 text-[11px] text-gray-500 text-center leading-relaxed">
              💡 Besoin d'aide ? Contacte le staff sur Discord pour annuler ou débloquer ta commande.
            </div>
          </div>
        </div>
      )}

      {/* Review submission modal */}
      {showReviewModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => !reviewSubmitting && setShowReviewModal(false)}
          data-testid="aqmopti-review-modal"
        >
          <div
            className="w-full max-w-md bg-zinc-900 border border-cyan-500/30 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-cyan-400">Réduction cumulable</div>
                <div className="text-lg font-semibold text-white mt-0.5">Envoyer ma preuve</div>
              </div>
              <button
                type="button"
                onClick={() => !reviewSubmitting && setShowReviewModal(false)}
                className="text-gray-400 hover:text-white"
                data-testid="aqmopti-review-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Source</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'discord', label: 'Avis Discord' },
                    { key: 'twitter', label: 'Avis X / Twitter' },
                    { key: 'tag', label: 'Tag serveur' },
                  ].map(p => {
                    const src = discountSources.find(s => s.key === p.key);
                    const locked = !src || src.status === 'approved' || src.status === 'pending';
                    return (
                      <button
                        key={p.key}
                        type="button"
                        disabled={locked}
                        onClick={() => setReviewPlatform(p.key)}
                        className={`px-2 py-2.5 rounded-lg border text-xs font-medium transition-colors ${
                          reviewPlatform === p.key
                            ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-300'
                            : 'bg-zinc-800/50 border-zinc-700 text-gray-400 hover:border-zinc-600'
                        } ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid={`aqmopti-review-platform-${p.key}`}
                      >
                        {p.label}
                        {src ? <span className="block text-[10px] mt-0.5 text-cyan-400/80">−{src.percent}%</span> : null}
                      </button>
                    );
                  })}
                </div>
                {reviewPlatform === 'tag' && (
                  <p className="text-[11px] text-gray-500 mt-2">
                    Ajoute le <span className="text-cyan-300">tag de notre serveur Discord</span> sur ton profil, puis envoie un screenshot de ton profil où on le voit.
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Screenshot (max 5 Mo)</label>
                <input
                  ref={reviewFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReviewFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="aqmopti-review-file-input"
                />
                <button
                  type="button"
                  onClick={() => reviewFileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 px-3 py-6 border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 rounded-lg text-gray-400 hover:text-cyan-300 transition-colors text-sm"
                  data-testid="aqmopti-review-file-btn"
                >
                  <Upload className="w-4 h-4" />
                  {reviewFile ? reviewFile.name : 'Choisir un screenshot'}
                </button>
              </div>

              {reviewError && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {reviewError}
                </div>
              )}

              <button
                type="button"
                onClick={submitReview}
                disabled={reviewSubmitting || !reviewFile}
                className="w-full px-4 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold text-sm transition-colors"
                data-testid="aqmopti-review-submit-btn"
              >
                {reviewSubmitting ? 'Envoi…' : 'Envoyer pour validation'}
              </button>

              <p className="text-[11px] text-gray-500 text-center leading-relaxed">
                Ton screenshot sera vérifié manuellement. Une fois validé, la réduction s'appliquera automatiquement au prochain paiement.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AqmOptiToolPage;
